import smoothscroll from "smoothscroll-polyfill";
import { CLASS_NAMES } from "./ClassNames";
import { InputType, LOGOUT_INLINE_URL, RegExpForInputTypes } from "./constants";
import { getArrayOfClass } from "./DomCrawlingHelper";
import { commonDebug } from "../bootstrap";

window.addEventListener("load", () => {
	(window as any).__forceSmoothScrollPolyfill__ = true;
	smoothscroll.polyfill();
});

export const sanitizeInputFieldValue = (e: KeyboardEvent, inputType: InputType, maxLength?: number) => {
	const target = e.target as HTMLInputElement;
	let value = Array.from(target.value)
		.filter((char) => RegExpForInputTypes.get(inputType).test(char))
		.join("")
		.replace(/[\t\n\r]/g, " ");

	// DEVNOTE JET-7635 Only disallow space on blur, otherwise space cannot be typed ever.
	if (e.type === "blur") {
		value = value.trim();
	}

	if (maxLength) value = value.substring(0, maxLength);

	target.value = value;

	return value;
};

const getSerializedObject = (object: any): string =>
	Object.keys(object)
		.filter((key) => object[key] !== undefined)
		.map((key) => `${key}=${encodeURIComponent(object[key] as string)}`)
		.join("&");

export const getRequestUrl = (queryParams: any): string => {
	const parts = Object.keys(queryParams)
		.filter((key) => queryParams[key] !== null)
		.map((key) =>
			typeof queryParams[key] === "object"
				? getSerializedObject(queryParams[key])
				: `${key}=${encodeURIComponent(queryParams[key] as string)}`
		);

	return parts.length ? `?${parts.join("&")}` : "";
};

export const toBoolean = (value: string | boolean | undefined): boolean => {
	if (!value) {
		return false;
	}
	return (typeof value !== "boolean" && value.toLowerCase() === "true") || (typeof value === "boolean" && value);
};

export const revealDelayedElements = (container: HTMLElement): void => {
	if (!container) {
		container = document.body;
	}
	container.classList.remove(CLASS_NAMES.delayed);
	const delayedElements = getArrayOfClass(container, CLASS_NAMES.delayed);
	if (delayedElements.length > 0) {
		delayedElements.forEach((e) => e.classList.remove(CLASS_NAMES.delayed));
	}
};

export const getParsedProperty = <T>(data: unknown): T => {
	if (data === "null") return undefined;
	if (data === "") return undefined;
	if (data === null) return undefined;
	if (data === "[]") return [] as T;
	return JSON.parse(data as string) as T;
};

export const clone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

const getScreenWidth = (): number => {
	const w = window;
	const doc = document;
	const e = doc.documentElement;
	const g = doc.getElementsByTagName("body")[0];
	const x = w.innerWidth || e.clientWidth || g.clientWidth;

	return x;
};

export const isMobileResolution = (): boolean => getScreenWidth() < 768;

export const isTabletOrMobileResolution = (): boolean => getScreenWidth() < 1024;

export const removeTrailingSlash = (str: string): string => str.replace(/\/$/, "");

export const logOutInline = async () => {
	await fetch(`${window.JetSmart.Settings.BookingUrl}${LOGOUT_INLINE_URL}`, {
		method: "GET",
		mode: "cors",
		credentials: "include",
	});
};

export const deleteLoginInfo = async () => {
	const baseUrl = "/api/UserData/LoginInfo";
	const fetchOptions: RequestInit = {
		method: "DELETE",
		credentials: "include",
		mode: "cors",
	};

	const fetchParameters = {
		options: fetchOptions,
		url: baseUrl,
	};

	try {
		await fetch(fetchParameters.url, fetchParameters.options);
	} catch (e) {
		commonDebug.error(e);
	}
};

export const capitalizeAndRemoveDot = (text: string): string =>
	(text.charAt(0).toUpperCase() + text.slice(1)).replace(".", "");

export const unique = <T>(array: T[]): T[] => {
	const set = new Set<T>(array);
	return Array.from(set);
};

export const DEBOUNCE_TIME = 250;

export const debounceCallback = <T>(delayedCallback: () => Promise<T>) =>
	new Promise<T>((resolve) => window.setTimeout(resolve, DEBOUNCE_TIME, delayedCallback()));

export const stripHtmlTags = (input: string): string => {
	const div = document.createElement("div");
	div.innerHTML = input;
	return div.textContent || "";
};

export const getSafeColorFromKontent = (color: string | undefined): string =>
	`#${
		Array.from(stripHtmlTags(color))
			?.filter((char) => /[A-Za-z0-9]/.test(char))
			.join("") || "000000"
	}`;
